// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Inline DOCX renderer for the contract-negotiation pipeline. Fetches the
 * pipeline-scoped annotated DOCX and renders it with docx-preview alongside
 * a Word-style review pane on the right. Each finding's quoted text gets
 * a colored highlight in the document; the review pane shows the comment
 * body in full, and clicking either side scrolls to the other.
 *
 * docx-preview's `renderComments` is intentionally OFF — its native tooltip
 * bubbles cut off long text and disappear on mouseout. The review pane is
 * the comment surface for users.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react'
import { renderAsync } from 'docx-preview'
import DOMPurify from 'dompurify'
import { Loader2 } from 'lucide-react'

// Defense-in-depth: the annotated.docx is byte-identical to the user's upload
// (python-docx edit-in-place preserves hyperlinks, embedded SVG, OLE refs,
// macros, etc.). We don't modify the file — instead we render it, then scrub
// the resulting DOM before the user can interact with it. DOMPurify in-place
// removes <script>, event handlers, `javascript:` hrefs, and inline SVG scripts.
const SAFE_URL_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'] as const

// 50 MB cap — large enough for any realistic contract, small enough to
// neutralize zip-bomb DoS attempts before docx-preview tries to parse them.
const MAX_DOCX_BYTES = 50 * 1024 * 1024

function sanitizeRenderedDocx(container: HTMLElement): void {
  DOMPurify.sanitize(container, { IN_PLACE: true, USE_PROFILES: { html: true, svg: true } })
  // DOMPurify already filters `javascript:` / `data:` URL schemes, but be
  // explicit so a future config change can't silently re-open the hole.
  container.querySelectorAll<HTMLAnchorElement>('a[href]').forEach(a => {
    try {
      const url = new URL(a.href, window.location.origin)
      if (!SAFE_URL_SCHEMES.includes(url.protocol as typeof SAFE_URL_SCHEMES[number])) {
        a.removeAttribute('href')
      }
    } catch {
      a.removeAttribute('href')
    }
  })
}

import type { NodeApiClient } from '@/app-shell/services'
import { cn } from '@/lib/utils'

import { useAnnotatedDocxBlob } from '../hooks/useAnnotatedDocxBlob'
import type { DocxAnnotation } from './DocxViewerSheet'

interface Props {
  apiClient: NodeApiClient
  orgId: string
  workspaceId: string
  documentId: string
  pipelineId: string
  version: number
  annotations: DocxAnnotation[]
  className?: string
  /** When set, only the matching <section> is shown (1-indexed). */
  targetPage?: number | null
  /** When set, the matching annotation pulses + its panel entry highlights. */
  activeAnnotationId?: string | null
}

const SEVERITY_BG: Record<DocxAnnotation['severity'], string> = {
  high: 'bg-red-200/60 hover:bg-red-200',
  medium: 'bg-amber-200/60 hover:bg-amber-200',
  low: 'bg-blue-200/60 hover:bg-blue-200',
}

const SEVERITY_BAR: Record<DocxAnnotation['severity'], string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-blue-500',
}

export const DocxView: FC<Props> = ({
  apiClient,
  orgId,
  workspaceId,
  documentId,
  pipelineId,
  version,
  annotations,
  className,
  targetPage,
  activeAnnotationId,
}) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const [rendering, setRendering] = useState(false)
  const [renderError, setRenderError] = useState<string | null>(null)
  const [renderedKey, setRenderedKey] = useState(0)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [markSectionByAnnotation, setMarkSectionByAnnotation] = useState<Map<string, number>>(new Map())
  const [docOrderByAnnotation, setDocOrderByAnnotation] = useState<Map<string, number>>(new Map())

  const marksRef = useRef<Map<string, HTMLElement>>(new Map())
  const panelItemsRef = useRef<Map<string, HTMLElement>>(new Map())

  // Shared fetch — react-query caches the blob across DocxView instances.
  const blobQuery = useAnnotatedDocxBlob(
    apiClient, orgId, workspaceId, documentId, pipelineId, version,
  )
  const blob = blobQuery.data
  const fetching = blobQuery.isPending
  const fetchError = blobQuery.error
    ? (blobQuery.error instanceof Error ? blobQuery.error.message : 'Failed to load DOCX')
    : null

  // Render the cached blob into our container whenever either becomes available.
  useEffect(() => {
    if (!container || !blob) return
    let cancelled = false
    setRendering(true)
    setRenderError(null)
    marksRef.current.clear()

    const run = async () => {
      try {
        if (blob.size > MAX_DOCX_BYTES) {
          throw new Error(`Document is too large to preview (${Math.round(blob.size / 1024 / 1024)} MB).`)
        }
        container.innerHTML = ''
        await renderAsync(blob, container, undefined, {
          className: 'docreview-docx',
          inWrapper: true,
          ignoreLastRenderedPageBreak: false,
          experimental: true,
          trimXmlDeclaration: true,
          useBase64URL: false,
          breakPages: true,
          renderChanges: true,
          renderComments: false,
        })
        if (cancelled) return
        sanitizeRenderedDocx(container)
        setRenderedKey(k => k + 1)
      } catch (err) {
        if (cancelled) return
        setRenderError(err instanceof Error ? err.message : 'Failed to render DOCX')
      } finally {
        if (!cancelled) setRendering(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [container, blob])

  const loading = fetching || rendering
  const error = fetchError ?? renderError

  // Wrap each annotation's quoted text in a clickable <mark>. Then record
  // which <section> (i.e., which page) each mark landed in so the sidebar
  // can filter when the modal is targeting a single page.
  useEffect(() => {
    if (!container || loading) return
    marksRef.current.clear()
    applyAnnotations(container, annotations, marksRef.current)

    const sections = Array.from(container.querySelectorAll<HTMLElement>('section'))
    const next = new Map<string, number>()
    for (const [id, mark] of marksRef.current) {
      const section = mark.closest('section')
      if (!(section instanceof HTMLElement)) continue
      const idx = sections.indexOf(section)
      if (idx >= 0) next.set(id, idx)
    }
    setMarkSectionByAnnotation(next)

    // querySelectorAll returns nodes in document tree order, which is the
    // reading order the sidebar should follow.
    const order = new Map<string, number>()
    container.querySelectorAll<HTMLElement>('mark[data-annotation-id]').forEach((el, i) => {
      const id = el.dataset.annotationId
      if (id && !order.has(id)) order.set(id, i)
    })
    setDocOrderByAnnotation(order)
  }, [container, loading, renderedKey, annotations])

  // Show only the target page when one is requested.
  useEffect(() => {
    if (!container || loading) return
    const sections = container.querySelectorAll<HTMLElement>('section')
    if (!sections.length) return
    if (targetPage == null || targetPage < 1 || targetPage > sections.length) {
      sections.forEach(s => { s.style.display = '' })
      return
    }
    sections.forEach((s, i) => {
      s.style.display = i === (targetPage - 1) ? '' : 'none'
    })
  }, [container, loading, renderedKey, targetPage])

  // Sort the sidebar by document reading order. Annotations whose text didn't
  // match (no mark, no entry in docOrderByAnnotation) fall to the end in their
  // original input order. When the modal targets a specific page, drop
  // anything not anchored to that page's section.
  const visibleAnnotations = useMemo(() => {
    const sorted = annotations
      .map((a, i) => ({ a, i, order: docOrderByAnnotation.get(a.id) }))
      .sort((x, y) => {
        if (x.order == null && y.order == null) return x.i - y.i
        if (x.order == null) return 1
        if (y.order == null) return -1
        return x.order - y.order
      })
      .map(x => x.a)
    if (targetPage == null) return sorted
    const sectionIdx = targetPage - 1
    return sorted.filter(a => markSectionByAnnotation.get(a.id) === sectionIdx)
  }, [annotations, targetPage, markSectionByAnnotation, docOrderByAnnotation])

  // Surface external activeAnnotationId selection.
  useEffect(() => {
    if (!activeAnnotationId) return
    setActiveId(activeAnnotationId)
    const mark = marksRef.current.get(activeAnnotationId)
    if (mark) {
      mark.scrollIntoView({ behavior: 'smooth', block: 'center' })
      mark.classList.add('docreview-annot-pulse')
      const t = window.setTimeout(() => mark.classList.remove('docreview-annot-pulse'), 1500)
      return () => window.clearTimeout(t)
    }
  }, [activeAnnotationId, renderedKey])

  // Scroll the sidebar to the active item. Separate from the mark-scroll effect
  // because panel <li> refs are populated only after applyAnnotations sets the
  // section map and the sidebar re-renders — re-running when visibleAnnotations
  // changes catches that delayed mount.
  useEffect(() => {
    if (!activeAnnotationId) return
    const panelItem = panelItemsRef.current.get(activeAnnotationId)
    if (panelItem) panelItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [activeAnnotationId, visibleAnnotations])

  // Clicking a mark in the document highlights the matching panel item.
  useEffect(() => {
    if (!container) return
    const onClick = (ev: MouseEvent) => {
      const target = (ev.target as HTMLElement | null)?.closest('mark[data-annotation-id]')
      if (!(target instanceof HTMLElement)) return
      const id = target.dataset.annotationId
      if (!id) return
      ev.preventDefault()
      setActiveId(id)
      const panelItem = panelItemsRef.current.get(id)
      if (panelItem) panelItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
    container.addEventListener('click', onClick)
    return () => container.removeEventListener('click', onClick)
  }, [container])

  const onSelectAnnotation = useCallback((id: string) => {
    setActiveId(id)
    const mark = marksRef.current.get(id)
    if (!mark) return
    mark.scrollIntoView({ behavior: 'smooth', block: 'center' })
    mark.classList.add('docreview-annot-pulse')
    window.setTimeout(() => mark.classList.remove('docreview-annot-pulse'), 1500)
  }, [])

  return (
    <div className={cn('flex gap-4 h-full min-h-0', className)}>
      <div className="flex-1 min-w-0 overflow-y-auto bg-muted/40 rounded p-4">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading document…
          </div>
        )}
        {error && !loading && (
          <div className="mx-auto max-w-md rounded border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}
        <div
          ref={setContainer}
          className={cn(
            'mx-auto max-w-[900px] rounded bg-white p-6 shadow-sm',
            loading && 'opacity-0',
          )}
        />
      </div>

      <aside className="hidden md:flex md:flex-col w-80 shrink-0 rounded border bg-card overflow-hidden h-full">
        <div className="px-3 py-2 border-b bg-muted/30 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Comments ({visibleAnnotations.length}
          {targetPage != null && annotations.length !== visibleAnnotations.length && (
            <> · page {targetPage}</>
          )}
          )
        </div>
        {visibleAnnotations.length === 0 ? (
          <p className="px-3 py-4 text-sm text-muted-foreground">
            {targetPage != null ? 'No comments on this page.' : 'No findings to display.'}
          </p>
        ) : (
          <ul className="flex-1 overflow-y-auto divide-y">
            {visibleAnnotations.map(a => (
              <li
                key={a.id}
                ref={node => {
                  if (node) panelItemsRef.current.set(a.id, node)
                  else panelItemsRef.current.delete(a.id)
                }}
                className={cn(
                  'flex gap-2 px-3 py-3 cursor-pointer hover:bg-accent/50 transition-colors',
                  activeId === a.id && 'bg-accent/60',
                )}
                onClick={() => onSelectAnnotation(a.id)}
              >
                <span className={cn('mt-1 h-2 w-2 shrink-0 rounded-full', SEVERITY_BAR[a.severity])} aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium leading-tight">{a.title}</div>
                  <div className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">
                    {a.comment}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Annotation wrapping (same multi-node walker the modal viewer uses, but with
// the aggressive normalizer so smart quotes / hyphens / spaces don't defeat
// matching).
// ---------------------------------------------------------------------------

function applyAnnotations(
  root: HTMLElement,
  annotations: DocxAnnotation[],
  anchors: Map<string, HTMLElement>,
): void {
  const used = new Set<Text>()
  for (const annotation of annotations) {
    const node = wrapFirstMatch(root, annotation, used)
    if (node) anchors.set(annotation.id, node)
  }
}

function wrapFirstMatch(
  root: HTMLElement,
  annotation: DocxAnnotation,
  _used: Set<Text>,
): HTMLElement | null {
  const needle = normalize(annotation.searchText)
  if (!needle) return null

  const texts: Text[] = []
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  while (walker.nextNode()) texts.push(walker.currentNode as Text)
  if (!texts.length) return null

  // Build a normalized concatenation, recording (node, rawOffset) for each
  // normalized character so we can map matches back to a DOM range.
  const positions: { node: Text; rawOffset: number }[] = []
  let normalized = ''
  let prevWs = true
  for (const text of texts) {
    const raw = text.data
    for (let i = 0; i < raw.length; i++) {
      const ch = raw[i]
      const folded = foldChar(ch)
      const isWs = folded === ' '
      const isAlnum = /[\p{L}\p{N}]/u.test(folded)
      if (!isAlnum && !isWs) {
        if (prevWs) continue
        prevWs = true
        normalized += ' '
        positions.push({ node: text, rawOffset: i })
        continue
      }
      if (isWs) {
        if (prevWs) continue
        prevWs = true
        normalized += ' '
        positions.push({ node: text, rawOffset: i })
      } else {
        prevWs = false
        normalized += folded.toLowerCase()
        positions.push({ node: text, rawOffset: i })
      }
    }
  }

  let idx = normalized.indexOf(needle)
  let matchLen = needle.length

  // Sliding anchor fallback: the LLM may have prepended a section label or
  // appended commentary that isn't in the DOM. Try every 10..4-word window
  // through the needle until one resolves.
  if (idx < 0) {
    const words = needle.split(' ').filter(Boolean)
    outer: for (let n = Math.min(10, words.length); n >= 4; n--) {
      const maxStart = Math.max(0, words.length - n)
      for (let start = 0; start <= maxStart; start++) {
        const anchor = words.slice(start, start + n).join(' ')
        const at = normalized.indexOf(anchor)
        if (at >= 0) {
          idx = at
          matchLen = anchor.length
          break outer
        }
      }
    }
  }
  if (idx < 0) return null

  const startPos = positions[idx]
  const endPos = positions[idx + matchLen - 1]
  if (!startPos || !endPos) return null

  const range = document.createRange()
  range.setStart(startPos.node, startPos.rawOffset)
  range.setEnd(endPos.node, Math.min(endPos.node.data.length, endPos.rawOffset + 1))
  return wrapRange(range, annotation)
}

function wrapRange(range: Range, annotation: DocxAnnotation): HTMLElement | null {
  const mark = document.createElement('mark')
  mark.dataset.annotationId = annotation.id
  mark.className = cn(
    'docreview-annot rounded px-0.5 transition-colors cursor-pointer',
    SEVERITY_BG[annotation.severity],
  )
  try {
    range.surroundContents(mark)
  } catch {
    const fragment = range.extractContents()
    mark.appendChild(fragment)
    range.insertNode(mark)
  }
  return mark
}

function normalize(s: string): string {
  return Array.from(s)
    .map(foldChar)
    .join('')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .trim()
}

function foldChar(ch: string): string {
  switch (ch) {
    case '‘': case '’': case '‚': case '‛': case '′': return "'"
    case '“': case '”': case '„': case '‟': case '″': return '"'
    case '–': case '—': case '−': return '-'
    case ' ': return ' '
    default: return ch
  }
}
